import { useState, useEffect, useCallback } from "react";
import { User, MapPin, Phone, Mail, Bell, BellOff, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ComuneCombobox from "../components/ComuneCombobox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
export default function Profile() {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user.name || "");
  const [comune, setComune] = useState(user.comune || "Fiano Romano");
  const [phone, setPhone] = useState(user.phone || "");
  const [saving, setSaving] = useState(false);
  const [follows, setFollows] = useState([]);

  const loadFollows = useCallback(async () => {
    try { const { data } = await api.get("/follows"); setFollows(data); } catch {}
  }, []);
  useEffect(() => { loadFollows(); }, [loadFollows]);

  const toggleFollow = async (comune) => {
    const isFollowing = follows.includes(comune);
    setFollows((f) => isFollowing ? f.filter((c) => c !== comune) : [...f, comune]);
    try {
      if (isFollowing) await api.delete(`/follows/${encodeURIComponent(comune)}`);
      else { await api.post("/follows", { comune }); toast.success(`Riceverai avvisi per ${comune}`); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); loadFollows(); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { name, comune, phone });
      updateUser(data);
      toast.success("Profilo aggiornato");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center"><User className="w-8 h-8" /></div>
        <div>
          <h1 className="text-2xl font-serif font-bold">{user.name}</h1>
          <p className="text-sm text-muted-foreground flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{user.email}</p>
        </div>
      </div>

      <form onSubmit={save} className="space-y-5">
        <div>
          <Label htmlFor="n">Nome pubblico</Label>
          <Input id="n" data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" />
        </div>
        <div>
          <Label className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />Comune / Zona</Label>
          <ComuneCombobox value={comune} onChange={setComune} testId="profile-comune" allowAltro placeholder="Cerca comune…" nearComune={user.comune || ""} className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="ph" className="flex items-center gap-1.5"><Phone className="w-4 h-4" />Telefono (privato)</Label>
          <Input id="ph" data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 h-11" />
          <p className="text-xs text-muted-foreground mt-1.5">Questo recapito e il tuo indirizzo non appaiono nel catalogo. Il telefono viene mostrato all'altra persona solo dopo che una proposta è stata accettata.</p>
        </div>
        <Button type="submit" data-testid="profile-save" disabled={saving} className="bg-primary hover:bg-primary/90">{saving ? "Salvataggio…" : "Salva modifiche"}</Button>
      </form>

      <div className="mt-10 pt-8 border-t border-border" data-testid="follows-section">
        <h2 className="text-lg font-serif font-semibold flex items-center gap-2"><Bell className="w-5 h-5" />Avvisi di zona</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-4">Cerca e aggiungi uno o più comuni: riceverai una notifica quando viene pubblicato un nuovo libro lì.</p>
        <div className="max-w-xs">
          <ComuneCombobox value="" onChange={(c) => { if (c && c !== "Altro" && !follows.includes(c)) toggleFollow(c); }} testId="follow-add" placeholder="Aggiungi un comune da seguire…" nearComune={comune || user.comune || ""} />
        </div>
        {follows.length > 0 ? (
          <div className="flex flex-wrap gap-2 mt-4">
            {follows.map((c) => (
              <button key={c} type="button" data-testid={`follow-${c}`} onClick={() => toggleFollow(c)}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm border bg-secondary text-secondary-foreground border-secondary transition-all hover:opacity-90">
                <Bell className="w-3.5 h-3.5" />{c}<X className="w-3.5 h-3.5 ml-0.5 opacity-70" />
              </button>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5"><BellOff className="w-3.5 h-3.5" />Non segui ancora nessun comune.</p>
        )}
      </div>
    </div>
  );
}
